import { Component, OnInit, ViewChild } from "@angular/core";
import { AggregationTransmitDataService } from "../aggregation-transmit-data.service";
import { TitleService } from "src/app/shared/services/title.service";
import { SharedDataService } from "src/app/shared/services/shared-data.service";
import { forkJoin, Observable } from "rxjs";
import {
  AggregationResultService,
  GTVService,
  KeyValueService,
} from "src/app/shared/api/services";
import { EngieClientId2namePipe } from "src/app/shared/pipes/engie-client-id2name.pipe";
import {
  Party,
  GTVPostUsageEventsResponseDTO,
} from "src/app/shared/api/models";
import { matchClientPartyRow } from "../../shared/classes/helpers";
import { ErrorHandlerComponent } from "src/app/shared/components/error-handler/error-handler.component";
import { PermissionsService } from "src/app/shared/services/permissions.service";
import { Persona } from "src/app/shared/enums/Persona";
import { GTVMinimumsResponseDTO } from 'src/app/shared/api/models/gtvminimums-response-dto';

enum sendOptions {
  none = "none",
  all = "all",
  single = "single",
}

@Component({
  selector: "e2cb-aggregation-transmit",
  templateUrl: "./aggregation-transmit.component.html",
  styleUrls: ["./aggregation-transmit.component.scss"],
})
export class AggregationTransmitComponent implements OnInit {
  // #region public variables
  emailStopValue: boolean = false;
  originalEmailStopValue: boolean = false;
  hasPermissionToStopEmails: boolean = false;
  @ViewChild(ErrorHandlerComponent, { static: true })
  errorHandlerComponent: ErrorHandlerComponent;
  billingPeriod: Date;
  sendClientChoice: sendOptions = sendOptions.none;
  sendOptions = sendOptions;
  showSpinner: boolean;
  clientIdList: string[] = [];
  clientsAvailable: Party[] = [];
  clientSelectDisabled: boolean;
  clientSelected: string = null;
  clientsSent = 0;
  billCyclesNotInSimulateStatusList: string[] = [];
  minimumsTrueUpResponse: GTVMinimumsResponseDTO = null;
  processingTrueUps: boolean = false;
  processingTrueUpsDone: boolean = false;
  displayDate: Date = new Date();
  matchClientPartyRow = matchClientPartyRow;
  _billingPeriod: Date;
  menuOpen: boolean = false;
  canSendTrueUpUsage: boolean = true;
  aviAccountNumber:string = "";
  salesforceSyncMessage:string = "";
  sendingToSalesforceSync:boolean = false;
  salesforceSyncError:boolean = false;
  aviBillingMinClientId: string = "";
  billingMinMessage: string = "";
  clearingBillingMin: boolean = false;
  billingMinError: boolean = false;

  get valid(): boolean {
    return (
      (this.clientSelected != null ||
        this.sendClientChoice === sendOptions.all) &&
      !this.menuOpen
    );
  }
  // #endregion
  // #region private variables
  private _engieClientId2namePipe: EngieClientId2namePipe;
  // #endregion
  // #region constructor
  constructor(
    private _titleService: TitleService,
    private _sharedDataService: SharedDataService,
    private _aggregegationResultService: AggregationResultService,
    private _gtvService: GTVService,
    private _keyValueService: KeyValueService,
    private _permissionsService: PermissionsService) {
      this._titleService.Subtitle = "Usage Data Transmission";
      this._billingPeriod = new Date();
      this._engieClientId2namePipe = new EngieClientId2namePipe(this._sharedDataService);
  }

  //#endregion
  // #region public functions
  async ngOnInit() {
    try {
      this.showSpinner = true;

      // Only super users are meant to see the email toggle
      this.hasPermissionToStopEmails = this._permissionsService.hasAtLeastOnePersona(
        [Persona.ClientBillingSuperUser]
      );

      // GetCurrentEmailState
      this.emailStopValue = await this._keyValueService
        .GetEmailStopState()
        .toPromise();
      this.originalEmailStopValue = this.emailStopValue.valueOf();

      // GetCurrentEmailState
      this.billCyclesNotInSimulateStatusList = await this._gtvService
        .GetBillCyclesNotInSimulateStatus()
        .toPromise();

      this.canSendTrueUpUsage = this.billCyclesNotInSimulateStatusList.length === 0;

      // await all the data gets
      await Promise.all([
        this._sharedDataService.PopulateAviClientIds(),
        (async () => {
          this._sharedDataService.aggregationXmitDataClientIds = (
            await this._aggregegationResultService
              .GetClientsReadyForTransmitResponse()
              .toPromise()
          ).body;
        })(),
      ]);

      this.clientIdList = [
        ...this._sharedDataService.aggregationXmitDataClientIds,
      ];

      // Map the clientIdList into a Party-object list for our search box
      this.clientsAvailable = this._sharedDataService.aggregationXmitDataClientIds.map(
        (id) => {
          return (
            this._sharedDataService.clientParties.find(
              (p) => p.clientParty.mailStop === id
            ) ||
            ({
              partyName: "No-Party-Found-For-ID" + id,
              clientParty: { mailStop: id },
            } as Party)
          );
        }
      );
    } catch (error) {
      this._sharedDataService.HandleError(error);
    } finally {
      this.showSpinner = false;
    }
  }

  onSubmit() {
    if (this.sendClientChoice === sendOptions.all) {
      this.postAggregationInChunks(this.clientIdList);
    } else if (this.clientSelected != null) {
      this.postAggregationInChunks(
        this.clientIdList.filter((elem) => elem === this.clientSelected)
      );
    }
  }

  sendTrueUpUsage() {
    console.log("Sending True-Up Usage...");
    this.postMiniumsTrueUp();
  }

  async postMiniumsTrueUp() {
    try {
      this.processingTrueUps = true;
      this.minimumsTrueUpResponse = await this._gtvService
        .ProcessMinimums()
        .toPromise();
      console.log("successful:", this.minimumsTrueUpResponse.successful);
      console.log("failuremessage:", this.minimumsTrueUpResponse.failureMessage);
    } catch (error) {
      this.minimumsTrueUpResponse = {
        successful: false,
        failureMessage: error.message,
        clientResults: []
      };
    } finally {
      this.processingTrueUps = false;
      this.processingTrueUpsDone = true;
    }
    console.log("Tried to send true-up...");
  }

  async postAggregationInChunks(clientIds: string[]) {
    let clientChunks = this.chunkArray(clientIds, 6) as string[][];
    let errors = { GtvErrors: [], APIErrors: [] };
    this.showSpinner = true;

    for (const ids of clientChunks) {
      let res = await this.postAggregation(ids);
      errors.GtvErrors = errors.GtvErrors.concat(res.GtvErrors);
      errors.APIErrors = errors.APIErrors.concat(res.APIErrors);
      this.clientsSent += ids.length;
    }

    if (errors.GtvErrors.length === 0 && errors.APIErrors.length === 0) {
      this.errorHandlerComponent.info(
        "Usage data sent to tract for selected client(s)",
        { title: "Success!" }
      );
    } else {
      this.errorHandlerComponent.error(
        "There were some errors during transmit, view more for details.",
        { fullmessage: errors }
      );
      console.log(errors); // output errors to console so users can find them there too.
    }

    this.showSpinner = false;
    this.clientsSent = 0;
  }

  async postAggregation(
    clientList: any[]
  ): Promise<{ GtvErrors: any[]; APIErrors: any[] }> {
    let httpRequestList: Promise<void | GTVPostUsageEventsResponseDTO>[] = [];
    let GTVErrors: any[] = [];
    let APIErrors: any[] = [];

    try {
      clientList.forEach((clientId) => {
        let httpRequest = this._gtvService
          .ProcessUsageEvents({ clientId: clientId })
          .toPromise()
          .catch((ex) => {
            APIErrors.push(ex);
          });
        httpRequestList.push(httpRequest);
      });
      let responseList = await Promise.all(httpRequestList);

      responseList.forEach((resp) => {
        let response = resp as GTVPostUsageEventsResponseDTO;
        if (
          response &&
          response.erred_events &&
          response.erred_events.length > 0
        ) {
          GTVErrors = GTVErrors.concat(response.erred_events);
        }
      });
    } catch (error) {
      APIErrors.push(error);
    }
    return {
      GtvErrors: GTVErrors,
      APIErrors: APIErrors,
    };
  }

  toggleCheckbox() {
    this.emailStopValue = !this.emailStopValue;
  }

  async onEmailToggleSubmit() {
    this.showSpinner = true;

    if (this.hasPermissionToStopEmails) {
      await this.postEmailToggle();
    }

    this.showSpinner = false;
  }

  async postEmailToggle() {
    let APIErrors: any[] = [];

    await this._keyValueService
      .UpdateEmailStopState({ newValue: this.emailStopValue })
      .toPromise()
      .catch((ex) => {
        APIErrors.push(ex);
      });

    if (APIErrors.length > 0) {
      this._sharedDataService.HandleError(APIErrors);
    } else {
      // disable the save button since we succeeded
      this.originalEmailStopValue = !this.originalEmailStopValue;
    }
  }

  async syncToGtv(clientId:string) {
    try {
      console.log(`Send account number to Salesforce sync: ${clientId}`);
      this.salesforceSyncMessage = "";
      this.sendingToSalesforceSync = true;
      this.salesforceSyncError = false;
      await this._gtvService
        .SyncClientToGtv({clientId: clientId})
        .toPromise();
      this.salesforceSyncMessage = `Sent account number ${clientId} to Salesforce Sync`;
    } catch (error) {
      this.salesforceSyncError = true;
      this.salesforceSyncMessage = `ERROR: An error occured while sending account number ${clientId} to Salesforce Sync`;
    } finally {
      this.sendingToSalesforceSync = false;
      this.aviAccountNumber = "";
    }
  }

  async clearBillingMin(clientId: string) {
    try {
      console.log(`Clearing billing minimum in GTV for client id: ${clientId}`);
      this.billingMinMessage = "";
      this.clearingBillingMin = true;
      this.billingMinError = false;

      await this._gtvService.ClearBillingMinimum({ clientId: clientId }).toPromise();

      this.billingMinMessage = `Successfully cleared billing minimum for client id ${clientId}`;
    } catch (error) {
      this.billingMinError = true;
      this.billingMinMessage = `ERROR: An error occurred while clearing billing minimum for client id ${clientId}`;
    } finally {
      this.clearingBillingMin = false;
      this.aviBillingMinClientId = ""; 
    }
  }

  numberOnly(event: { which: any; keyCode: any; }): boolean {
    const charCode = (event.which) ? event.which : event.keyCode;
    if (charCode > 31 && (charCode < 48 || charCode > 57)) {
      return false;
    }
    return true;
  }

  // #endregion
  // #region private functions
  private chunkArray(array, chunkSize) {
    return Array.from(
      { length: Math.ceil(array.length / chunkSize) },
      (_, index) => array.slice(index * chunkSize, (index + 1) * chunkSize)
    );
  }
  // #endregion
}
